<script setup lang="ts">
import { TriangleAlert } from "lucide-vue-next";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import SproutGlyph from "@/components/ui/SproutGlyph.vue";
import {
  BOOT_COPY,
  bootEscalation,
  formatElapsed,
  milestoneFraction,
  type BootMilestone,
} from "@/lib/bootMilestones";
import type { SessionMode } from "@/lib/focusSession";

/**
 * The session boot screen (PRD PE-8.2).
 *
 * Presentational by contract: the overlay container owns the requests and
 * reports where they have actually got to; this component renders that and
 * nothing else. There are no emits, so retry after a failure is the
 * container's affordance, not this screen's.
 *
 * **The ring is driven by `milestone`, never by a clock.** The loader this
 * replaced advanced three scripted lines on a 3.5s interval whether or not the
 * backend had done anything, which read as finished mid-request and as hung
 * once it ran out of script. The wait is worth covering honestly: PRD §8.1
 * measured 16.2s TTFB on a scale-from-zero versus 0.104s warm, and generation
 * sits on top of that.
 */

const props = defineProps<{ mode: SessionMode; milestone: BootMilestone }>();

const copy = computed(() => BOOT_COPY[props.mode]);
const settled = computed(
  () => props.milestone === "ready" || props.milestone === "failed",
);
const fraction = computed(() => milestoneFraction(props.milestone));
const heading = computed(() =>
  props.milestone === "failed" ? copy.value.failedTitle : copy.value.title,
);

/*
 * Wall clock, not progress. `fraction` above is the only thing that moves the
 * ring, and it moves only when the container reports a real event. These two
 * counters exist so that (a) a wait which has genuinely gone long can say so
 * plainly instead of looking stuck, and (b) the student can see a number
 * changing and know the screen is alive.
 *
 * `milestoneSeconds` resets on every milestone so the escalation thresholds
 * mean "this step has been running a while"; `totalSeconds` is what the
 * student has actually waited, which is the number worth showing them.
 */
const milestoneSeconds = ref(0);
const totalSeconds = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;

function syncTicker(): void {
  if (!settled.value && ticker === null) {
    ticker = setInterval(() => {
      milestoneSeconds.value++;
      totalSeconds.value++;
    }, 1000);
  } else if (settled.value && ticker !== null) {
    clearInterval(ticker);
    ticker = null;
  }
}

onMounted(syncTicker);

watch(
  () => props.milestone,
  () => {
    milestoneSeconds.value = 0;
    syncTicker();
  },
);

onBeforeUnmount(() => {
  if (ticker !== null) clearInterval(ticker);
});

const escalation = computed(() =>
  bootEscalation(props.mode, props.milestone, milestoneSeconds.value),
);
const elapsedLabel = computed(() => formatElapsed(totalSeconds.value));
</script>

<template>
  <div class="boot rl-anim-pop" :data-milestone="milestone">
    <div class="ring">
      <svg
        class="ring-svg"
        viewBox="0 0 36 36"
        aria-hidden="true"
        focusable="false"
      >
        <circle class="ring-track" cx="18" cy="18" r="15.9" />
        <!--
          Idle flourish: a pale arc that breathes while a request is in flight,
          so the ring has life during a long wait. Deliberately in the mint
          family rather than the fill green, and deliberately looping, so it
          reads as "something is happening" and never as "this much is done".
        -->
        <circle
          v-if="!settled"
          class="ring-flourish"
          cx="18"
          cy="18"
          r="15.9"
        />
        <circle
          class="ring-fill"
          cx="18"
          cy="18"
          r="15.9"
          :style="{ strokeDashoffset: 100 - fraction * 100 }"
        />
      </svg>
      <div class="ring-core" :class="{ 'rl-breathe': !settled }">
        <TriangleAlert
          v-if="milestone === 'failed'"
          class="core-failed"
          :size="48"
          :stroke-width="1.6"
        />
        <SproutGlyph v-else :size="60" />
      </div>
    </div>

    <h2 class="boot-title">{{ heading }}</h2>
    <p class="boot-status" role="status" aria-live="polite">
      {{ copy.status[milestone] }}
    </p>

    <div v-if="!settled" class="boot-bar" aria-hidden="true">
      <span class="boot-bar-fill" />
    </div>

    <p v-if="escalation" class="boot-escalation">
      {{ escalation }}
      <span class="boot-elapsed">{{ elapsedLabel }}</span>
    </p>
    <p v-else-if="milestone !== 'failed'" class="boot-note">
      {{ copy.reassurance }}
    </p>
  </div>
</template>

<style scoped>
/* rl-qe6: the boot screen is the whole overlay, not a card in its corner.
   It is centred by the host (SessionOverlay's .booting) and sized to read
   from across a desk; the ring scales with the viewport between 120px and
   184px so a phone keeps the copy above the fold. */
.boot {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  text-align: center;
}

.ring {
  position: relative;
  width: clamp(120px, 22vw, 184px);
  aspect-ratio: 1;
  margin: 0 auto var(--sp-8);
}

/* Starts the fill at 12 o'clock. */
.ring-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.ring-track,
.ring-flourish,
.ring-fill {
  fill: none;
  stroke-width: 2.4;
}

.ring-track {
  stroke: var(--rl-bg-muted);
}

/* r = 15.9 gives a circumference of ~100, so the dash length is the
   percentage directly and needs no per-size circumference maths. */
.ring-flourish,
.ring-fill {
  stroke-dasharray: 100;
  stroke-linecap: round;
}

.ring-flourish {
  stroke: var(--rl-mint-300);
  stroke-dashoffset: 100;
  animation: rl-ring-grow 2.7s var(--ease-standard) infinite alternate;
}

.ring-fill {
  stroke: var(--rl-green-600);
  transition: stroke-dashoffset var(--t-slow);
}

/* A failure is not partial success: the fill is already 0 for `failed`
   (milestoneFraction), and the core switches from sprout to warning. */
.ring-core {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--rl-green-600);
}

[data-milestone="failed"] .ring-core {
  color: var(--rl-warn-fg);
}

.core-failed {
  display: block;
}

.boot-title {
  margin: 0 0 var(--sp-3);
  font-size: var(--rl-text-3xl);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--rl-fg-1);
}

.boot-status {
  margin: 0;
  min-height: 24px;
  font-size: var(--rl-text-lg);
  font-weight: 500;
  color: var(--rl-fg-2);
}

[data-milestone="failed"] .boot-status {
  max-width: 340px;
  margin: 0 auto;
  line-height: 1.5;
  color: var(--rl-warn-fg);
}

.boot-bar {
  position: relative;
  width: 320px;
  max-width: 100%;
  height: 6px;
  margin: var(--sp-6) auto 0;
  overflow: hidden;
  background: var(--rl-bg-muted);
  border-radius: var(--radius-pill);
}

.boot-bar-fill {
  position: absolute;
  top: 0;
  width: 42%;
  height: 100%;
  background: var(--rl-green-600);
  border-radius: var(--radius-pill);
  animation: rl-indeterminate 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.boot-note,
.boot-escalation {
  max-width: 400px;
  margin: var(--sp-6) auto 0;
  font-size: var(--rl-text-md);
  line-height: 1.5;
  color: var(--rl-fg-3);
}

.boot-escalation {
  color: var(--rl-fg-2);
}

.boot-elapsed {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
  white-space: nowrap;
}

/* The looping helpers (.rl-breathe) are stopped by the global query in
   style.css; these are this component's own animations, so it stops them
   itself. The bar carries no information beyond "still working" — which the
   status line and the elapsed count already say in words — so it goes rather
   than freezing mid-track, and the ring still lands on the right fill, it
   just stops sliding there. */
@media (prefers-reduced-motion: reduce) {
  .ring-flourish,
  .boot-bar {
    display: none;
  }

  .ring-fill {
    transition: none;
  }
}
</style>
